import { FC, useState } from 'react';
import {
  Package,
  Building2,
  Users,
  Share2,
  FileText,
  Target,
  MessageSquare,
  Calendar,
  ExternalLink,
  Edit2,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { SocialConnectionsPanel } from '@/components/social';
import { Step7Calendly } from '@/components/wizard/Step7Calendly';
import { wizardService } from '@/services';
import { useToast } from '@/contexts/ToastContext';
import type { WizardStatus } from '@/hooks/useWizard';

interface WizardStepDetailViewProps {
  stepNumber: number;
  wizardData: WizardStatus['data'] | undefined;
  onEdit: () => void;
  onSuccess?: () => Promise<void>;
}

const stepConfig = {
  1: { title: 'Product/Service', icon: Package },
  2: { title: 'Company Profile', icon: Building2 },
  3: { title: 'Competitors', icon: Users },
  4: { title: 'Social Connections', icon: Share2 },
  5: { title: 'Content Generation', icon: FileText },
  6: { title: 'Sales Targets', icon: Target },
  7: { title: 'Pitch Strategy', icon: MessageSquare },
  8: { title: 'Connect Calendar', icon: Calendar },
};

export const WizardStepDetailView: FC<WizardStepDetailViewProps> = ({
  stepNumber,
  wizardData,
  onEdit,
  onSuccess,
}) => {
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStep8Submit = async (formData: any) => {
    try {
      setIsSubmitting(true);
      await wizardService.saveStep8(formData);
      addToast('Calendar settings updated successfully', 'success');
      if (onSuccess) await onSuccess();
    } catch (err) {
      addToast('Failed to save calendar settings', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const config = stepConfig[stepNumber as keyof typeof stepConfig];
  const Icon = config.icon;
  const data = wizardData || {};

  const renderStepContent = () => {
    switch (stepNumber) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.productOrService || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.industry || 'N/A'}</p>
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company Description</span>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed whitespace-pre-wrap">{data.description || 'N/A'}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Service Description</span>
              <p className="text-sm text-gray-700 mt-1.5 leading-relaxed whitespace-pre-wrap">{data.serviceDescription || 'N/A'}</p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            {/* Country & Timezone */}
            {(data.country || data.timezone) && (
              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Country</span>
                  <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.country || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Timezone</span>
                  <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.timezone || 'N/A'}</p>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Logo</span>
              {data.logoUrl ? (
                <div className="w-24 h-24 border border-gray-200 rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img src={data.logoUrl} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">
                Key People ({data.keyPeople?.length || 0})
              </span>
              {data.keyPeople && data.keyPeople.length > 0 ? (
                <div className="space-y-2.5">
                  {data.keyPeople.map((person, index) => (
                    <div key={index} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
                      {person.pictureUrl ? (
                        <img
                          src={person.pictureUrl}
                          alt={`${person.name} ${person.familyName || ''}`}
                          className="w-9 h-9 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {person.name} {person.familyName || ''}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{person.role || 'N/A'}</p>
                        {person.linkedinUrl ? (
                          <a
                            href={person.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mt-1 font-medium"
                          >
                            LinkedIn <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1">LinkedIn: N/A</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">Brand Colors</span>
              {data.brandColors && (data.brandColors.primary || data.brandColors.secondary || data.brandColors.accent) ? (
                <div className="flex gap-4">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.primary || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.primary || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.secondary || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.secondary || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-10 h-10 rounded border border-gray-300 shadow-sm"
                      style={{ backgroundColor: data.brandColors.accent || '#f3f4f6' }}
                    />
                    <span className="text-xs text-gray-600 font-mono font-medium">{data.brandColors.accent || 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Brand Guidelines</span>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{data.brandGuidelines || 'N/A'}</p>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            {data.competitors && data.competitors.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.competitors.map((competitor) => (
                  <span
                    key={competitor.id}
                    className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                  >
                    {competitor.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 font-medium">N/A</p>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 mb-4">
              Connect your social accounts to enable automated posting
            </p>
            <SocialConnectionsPanel
              showLinkedIn={true}
              showFacebook={true}
              showInstagram={true}
              showLinkedInPageSelector={false}
              compact={true}
            />
            {data.linkedinUrl && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Selected Company Page</span>
                <a
                  href={data.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-700 hover:text-gray-900 inline-flex items-center gap-2 font-medium group mt-1"
                >
                  <span className="truncate max-w-md">{data.linkedinUrl}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </a>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Post Types</span>
              {data.contentTypes && data.contentTypes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {data.contentTypes.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                    >
                      {typeof type === 'object' ? type.category : type}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">N/A</p>
              )}
            </div>
            <div className="pt-4 border-t border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Daily Post Generation</span>
              <div className="flex items-center gap-3">
                <div className="text-2xl">
                  {Array.from({ length: data.dailyPostCount ?? 3 }).map((_, i) => (
                    <span key={i}>📄</span>
                  ))}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {data.dailyPostCount ?? 3} {(data.dailyPostCount ?? 3) === 1 ? 'post' : 'posts'} daily
                  </div>
                  <div className="text-xs text-gray-500">
                    Auto-generated content
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Name</span>
              <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.name || 'N/A'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.industry || 'N/A'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Role</span>
                <p className="text-sm text-gray-900 mt-1.5 font-medium">{data.salesTarget?.role || 'N/A'}</p>
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div>
            {data.pitchStrategy ? (
              <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                {data.pitchStrategy}
              </span>
            ) : (
              <p className="text-sm text-gray-400 font-medium">N/A</p>
            )}
          </div>
        );

      case 8:
        return (
          <Step7Calendly
            initialData={{ calendlyLink: data.calendlyLink }}
            onNext={handleStep8Submit}
            isSubmitting={isSubmitting}
          />
        );

      default:
        return <p className="text-sm text-gray-400 font-medium">Invalid step</p>;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 shadow-sm">
            <Icon className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {config.title}
            </h3>
          </div>
        </div>
        {stepNumber !== 8 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            className="text-xs font-medium"
          >
            <Edit2 className="w-3.5 h-3.5" />
            <span className="ml-1.5">Edit</span>
          </Button>
        )}
      </div>
      <div className="p-6">
        {renderStepContent()}
      </div>
    </div>
  );
};

